// Minimal vanilla-JS interactions that replace Bootstrap's JS components on
// Tailwind/daisyUI pages. daisyUI handles dropdowns, drawers, and collapses with
// CSS only; this module covers the few behaviours that still need JS:
//
//   - Dismissible alerts:  <button data-dismiss-alert> inside a daisyUI .alert
//   - Native <dialog> open/close:
//       <button data-dialog-open="my-dialog">    -> dialog.showModal()
//       <button data-dialog-close>               -> closest dialog .close()
//   - Reveal a companion field when a <select> hits a given value:
//       <select data-reveal-on="__other__" data-reveal-target="other_wrap">
//   - Confirm before a destructive submit:
//       <form data-confirm="Delete X? This cannot be undone.">
//
// The charge-modal's fetch + React-mount orchestration is layered on top of this
// in the admin migration phase.

function onClick(
  selector: string,
  handler: (el: HTMLElement, ev: Event) => void,
) {
  document.addEventListener("click", (ev) => {
    const target = (ev.target as HTMLElement | null)?.closest(selector);
    if (target instanceof HTMLElement) handler(target, ev);
  });
}

// Dismiss the enclosing daisyUI alert.
onClick("[data-dismiss-alert]", (btn) => {
  btn.closest(".alert")?.remove();
});

// Open a native <dialog> by id.
onClick("[data-dialog-open]", (btn) => {
  const id = btn.getAttribute("data-dialog-open");
  if (!id) return;
  const dialog = document.getElementById(id);
  if (dialog instanceof HTMLDialogElement) dialog.showModal();
});

// Close the enclosing native <dialog>.
onClick("[data-dialog-close]", (btn) => {
  (btn.closest("dialog") as HTMLDialogElement | null)?.close();
});

// Confirm before submitting a form marked destructive. The message lives in a
// data attribute rather than an inline onsubmit= handler: the message embeds
// user-authored values (producer names, member names), and inside an attribute
// those are HTML-escaped but then entity-decoded by the parser *before* the JS
// is compiled -- so an apostrophe in a name could terminate the string literal
// and anything after it would execute. Read as data, it is never parsed as code.
document.addEventListener("submit", (ev) => {
  const form = ev.target;
  if (!(form instanceof HTMLFormElement)) return;

  const message = form.getAttribute("data-confirm");
  if (message && !window.confirm(message)) ev.preventDefault();
});

// Show/hide the element named by data-reveal-target depending on whether the
// <select> currently sits on data-reveal-on. Used by the wine form's "Other…"
// options, where picking Other reveals a free-text box.
function syncReveal(select: HTMLSelectElement) {
  const targetId = select.getAttribute("data-reveal-target");
  const revealOn = select.getAttribute("data-reveal-on");
  if (!targetId || revealOn === null) return;

  document
    .getElementById(targetId)
    ?.classList.toggle("hidden", select.value !== revealOn);
}

document.addEventListener("change", (ev) => {
  const target = ev.target;
  if (
    target instanceof HTMLSelectElement &&
    target.hasAttribute("data-reveal-on")
  ) {
    syncReveal(target);
  }
});

// Run once on load so an edit form that was saved with "Other" comes back with
// the free-text box already visible.
document.addEventListener("DOMContentLoaded", () => {
  document
    .querySelectorAll<HTMLSelectElement>("select[data-reveal-on]")
    .forEach(syncReveal);
});

// Collapse/expand the admin sidebar to an icon-only rail (desktop). State lives
// on <html data-sidebar="collapsed"> and is persisted so it survives navigation;
// base-admin restores it synchronously in <head> to avoid a flash.
onClick("[data-sidebar-toggle]", () => {
  const root = document.documentElement;
  const collapsed = root.getAttribute("data-sidebar") === "collapsed";
  if (collapsed) {
    root.removeAttribute("data-sidebar");
    localStorage.removeItem("tg-sidebar-collapsed");
  } else {
    root.setAttribute("data-sidebar", "collapsed");
    localStorage.setItem("tg-sidebar-collapsed", "1");
  }
});

export {};
